import { useRef, useEffect, useCallback } from "react";
import { useApolloClient } from "@apollo/client/react";
import { MapClustersDocument } from "#app/lib/data";
import type { MapPin } from "#app/components/map/map-types";
import { clusterToPin } from "./use-map-clusters";
import type { Omittable } from "@salestrak/ui";

/** Tight bounds for the focused query (~550m radius) */
const SEARCH_RADIUS = 0.005;

// Callers build this inline from route search params, where `addressId` is
// routinely absent; nothing reads a missing key differently from an explicit
// `undefined`, so the member relaxes.
type PendingSearch = Omittable<{ lat: number; lng: number; addressId?: string }>;

type UsePendingPinSearchOptions = Omittable<{
  pins: MapPin[];
  onMatch: (pin: MapPin) => void;
  onFallback: (search: PendingSearch) => void;
  where?: Record<string, unknown>;
}>;

type PendingState = {
  search: PendingSearch;
  /** Pin data from focused query — null until the focused query returns. */
  confirmedPin: MapPin | null;
  /** True once rawPins has updated at least once since the search started. */
  regionLoaded: boolean;
};

function findMatch(
  pins: MapPin[],
  search: PendingSearch,
): MapPin | undefined {
  // Prefer addressId when the caller provided one — it's a stable identifier
  // unaffected by float precision drift between the address entity and the
  // clustered pin's stored coordinates.
  if (search.addressId) {
    const byId = pins.find((p) => p.addressId === search.addressId);
    if (byId) return byId;
  }
  // Exact coord equality: an address and its pins come from the same Google
  // Place result, so their stored coordinates should be identical. Any drift
  // means a different pin — don't hijack the match.
  return pins.find(
    (p) => p.latitude === search.lat && p.longitude === search.lng,
  );
}

/**
 * After an address search pans the map, fetches pin data for that specific
 * address via a focused MapClusters query (tight bounds, no clustering).
 *
 * Opens the pin only once both the focused query AND the region data have
 * arrived — whichever completes last triggers the open.
 */
export function usePendingPinSearch({
  pins,
  onMatch,
  onFallback,
  where,
}: UsePendingPinSearchOptions) {
  const client = useApolloClient();
  const requestIdRef = useRef(0);
  const onMatchRef = useRef(onMatch);
  const onFallbackRef = useRef(onFallback);
  const pinsRef = useRef(pins);
  const whereRef = useRef(where);
  onMatchRef.current = onMatch;
  onFallbackRef.current = onFallback;
  pinsRef.current = pins;
  whereRef.current = where;

  const pendingRef = useRef<PendingState | null>(null);

  const cancel = useCallback(() => {
    requestIdRef.current += 1;
    pendingRef.current = null;
  }, []);

  // Invalidate any in-flight query on unmount
  useEffect(() => () => cancel(), [cancel]);

  /** Open the pin — prefers the map version, falls back to focused query data. */
  const resolve = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending?.confirmedPin) return;

    const onMap = findMatch(pinsRef.current, {
      lat: pending.confirmedPin.latitude,
      lng: pending.confirmedPin.longitude,
      addressId: pending.confirmedPin.addressId,
    });

    pendingRef.current = null;
    onMatchRef.current(onMap ?? pending.confirmedPin);
  }, []);

  // When rawPins update, mark region as loaded and resolve if ready
  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending) return;

    const match = findMatch(pins, pending.search);
    if (match && pending.confirmedPin) {
      pendingRef.current = null;
      onMatchRef.current(match);
      return;
    }

    pending.regionLoaded = true;

    if (pending.confirmedPin) {
      resolve();
    }
  }, [pins, resolve]);

  const start = useCallback(
    (search: PendingSearch) => {
      cancel();

      // Check current pins immediately — the match may already be loaded
      const immediateMatch = findMatch(pinsRef.current, search);
      if (immediateMatch) {
        onMatchRef.current(immediateMatch);
        return;
      }

      // Set up pending state — both paths (focused query + pins watcher)
      // will check this and resolve when both are ready.
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      pendingRef.current = {
        search,
        confirmedPin: null,
        regionLoaded: false,
      };

      client
        .query({
          query: MapClustersDocument,
          variables: {
            bounds: {
              north: search.lat + SEARCH_RADIUS,
              south: search.lat - SEARCH_RADIUS,
              east: search.lng + SEARCH_RADIUS,
              west: search.lng - SEARCH_RADIUS,
            },
            where: whereRef.current,
            options: { zoom: 20, clusterThreshold: 999 },
          },
          fetchPolicy: "no-cache",
        })
        .then(({ data }) => {
          if (requestIdRef.current !== requestId) return;

          const focusedPins = (data?.mapClusters ?? []).map(clusterToPin);

          const match = findMatch(focusedPins, search);

          if (!match) {
            // The region query may have already loaded the pin while the
            // focused query was in flight — check before falling back.
            const onMap = findMatch(pinsRef.current, search);
            pendingRef.current = null;
            if (onMap) {
              onMatchRef.current(onMap);
            } else {
              onFallbackRef.current(search);
            }
            return;
          }

          const pending = pendingRef.current;
          if (!pending) return;

          pending.confirmedPin = match;

          if (pending.regionLoaded) {
            resolve();
          }
        })
        .catch((err) => {
          if (requestIdRef.current !== requestId) return;
          console.warn("[usePendingPinSearch] focused query failed:", err);
          const onMap = findMatch(pinsRef.current, search);
          pendingRef.current = null;
          if (onMap) {
            onMatchRef.current(onMap);
          } else {
            onFallbackRef.current(search);
          }
        });
    },
    [cancel, client, resolve],
  );

  return { start, cancel };
}
